/**
 * Complete the Look, manual source (see snippets/complete-the-look.liquid).
 *
 * The complementary products the merchandising team picked in Search & Discovery are only
 * exposed on the product recommendations route, so the tiles are fetched from
 * sections/section-rendering-complete-the-look.liquid after the page loads. The section is
 * rendered `hidden` and is revealed only once at least one tile has arrived: a product
 * with no picks (or a failed request) leaves nothing on the page — no heading, no gap.
 */
class CompleteTheLookRecs extends HTMLElement {
  /** @type {AbortController | null} */
  #abortController = null;

  /** Arrow and scroll listeners are bound once, however often the element reconnects. */
  #wired = false;

  static get observedAttributes() {
    return ['data-url'];
  }

  connectedCallback() {
    this.#reload();
  }

  disconnectedCallback() {
    this.#abortController?.abort();
  }

  /**
   * A colour change on the PDP (assets/color-swatch-nav.js, or a combined listing in
   * assets/variant-picker.js) morphs `main` in place: this element survives, so
   * connectedCallback never runs again, but the morph resets the section to the server's
   * hidden, empty shell and points `data-url` at the new product. Fetch that product's tiles.
   * @param {string} name
   * @param {string | null} oldValue
   * @param {string | null} newValue
   */
  attributeChangedCallback(name, oldValue, newValue) {
    // The initial value arrives here during upgrade, before connectedCallback loads it.
    if (oldValue === null || oldValue === newValue || !this.isConnected) return;
    this.#reload();
  }

  #reload() {
    this.#abortController?.abort();
    this.#abortController = new AbortController();
    this.#load(this.#abortController.signal);
  }

  /** @returns {HTMLElement | null} */
  get #root() {
    return this.closest('.complete-the-look');
  }

  /** @returns {HTMLElement | null} */
  get #track() {
    return this.querySelector('[data-ctl-track]');
  }

  /**
   * @param {AbortSignal} signal
   */
  async #load(signal) {
    const url = this.dataset.url;
    const track = this.#track;
    if (!url || !track) return;

    try {
      const response = await fetch(url, { signal });
      if (!response.ok) return;

      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const items = html.querySelectorAll('[data-complete-the-look-list] > [data-complete-the-look-item]');
      if (signal.aborted || !items.length) return;

      track.replaceChildren(...items);
      this.#reveal();
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
      // Leave the section hidden: an empty Complete the Look is the correct fallback.
    }
  }

  #reveal() {
    const root = this.#root;
    const track = this.#track;
    if (!root || !track) return;

    root.hidden = false;
    if (this.#wired) return;
    this.#wired = true;

    const arrows = /** @type {HTMLElement | null} */ (root.querySelector('[data-ctl-arrows]'));
    const prev = /** @type {HTMLButtonElement | null} */ (root.querySelector('[data-ctl-scroll="prev"]'));
    const next = /** @type {HTMLButtonElement | null} */ (root.querySelector('[data-ctl-scroll="next"]'));

    const updateArrows = () => {
      const max = track.scrollWidth - track.clientWidth;
      // Every tile already fits: nothing to slide to, so no arrows at all.
      if (arrows) arrows.hidden = max <= 1;
      if (prev) prev.disabled = track.scrollLeft <= 1;
      if (next) next.disabled = max <= 1 || track.scrollLeft >= max - 1;
    };

    /** @param {1 | -1} direction */
    const scrollByCard = (direction) => {
      const slide = track.querySelector('.complete-the-look__slide');
      const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
      const amount = slide ? slide.getBoundingClientRect().width + gap : track.clientWidth;
      track.scrollBy({ left: direction * amount, behavior: 'smooth' });
    };

    prev?.addEventListener('click', () => scrollByCard(-1));
    next?.addEventListener('click', () => scrollByCard(1));
    track.addEventListener('scroll', updateArrows, { passive: true });
    new ResizeObserver(updateArrows).observe(track);
    updateArrows();
  }
}

if (!customElements.get('complete-the-look-recs')) {
  customElements.define('complete-the-look-recs', CompleteTheLookRecs);
}
